import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useIntl } from 'react-intl';
import { RootState } from '../../reducers';
import { fetchUnreadCount, receiveLiveNotification } from '../../actions/notifications';
import { subscribeToUserNotifications, disconnectEcho } from '../../services/echo';
import './NotificationBell.scss';

// Slow safety-net poll — only runs as a reconciliation pass in case the
// WebSocket missed an event (network drop, reconnect lag). When Echo is
// healthy this is mostly a no-op.
const POLL_INTERVAL_MS = 60_000;

const BellIcon: React.FC<{ hasUnread: boolean }> = ({ hasUnread }) => (
    <svg
        className={`bell__svg ${hasUnread ? 'is-active' : ''}`}
        xmlns="http://www.w3.org/2000/svg"
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
);

/**
 * Top-of-sidebar bell. Clicking routes to the full notifications archive page
 * — no dropdown, since notification bodies in this app are text-heavy
 * (block reasons + client / project / article context) and don't fit a
 * 380px popover without truncation. The badge + 30s poll keep the
 * "you have new stuff" signal alive without needing a peek surface.
 */
const NotificationBell: React.FC = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const intl = useIntl();
    const unreadCount = useSelector((s: RootState) => s.notifications.unreadCount);
    const isAuthenticated = useSelector((s: RootState) => (s.user as any)?.isAuthenticated);
    // Current user id — the rest of the app reads this from localStorage
    // (set on login), not Redux. Following the same pattern here so the bell
    // doesn't depend on a slightly different source of truth.
    const currentUserId = typeof window !== 'undefined' ? Number(localStorage.getItem('mikaUserId')) : 0;
    const pollTimer = useRef<number | null>(null);

    // Slow safety-net poll — only runs as a reconciliation pass in case the
    // WebSocket missed an event. Runs at 60s when authed.
    useEffect(() => {
        if (!isAuthenticated) return;
        (dispatch as any)(fetchUnreadCount());
        pollTimer.current = window.setInterval(() => {
            (dispatch as any)(fetchUnreadCount());
        }, POLL_INTERVAL_MS);
        return () => {
            if (pollTimer.current) window.clearInterval(pollTimer.current);
            pollTimer.current = null;
        };
    }, [dispatch, isAuthenticated]);

    // Live updates via Echo private channel. On every incoming notification:
    //   1. Append to pendingNew so the archive page's "N new" pill shows
    //   2. Bump the bell badge
    // When Echo isn't configured (env vars missing), the subscribe helper
    // returns null and the slow poll above remains the only update path.
    useEffect(() => {
        if (!isAuthenticated || !currentUserId) return;
        const unsubscribe = subscribeToUserNotifications(currentUserId, (payload: any) => {
            (dispatch as any)(receiveLiveNotification(payload));
        });
        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [dispatch, isAuthenticated, currentUserId]);

    // Tear down the Echo connection on logout so the next user's session
    // doesn't inherit the previous bearer token.
    useEffect(() => {
        if (isAuthenticated) return;
        disconnectEcho();
    }, [isAuthenticated]);

    // Listen for navigation messages from the service worker — when the user
    // clicks an OS notification banner, the SW posts a message telling this
    // tab to navigate to the action URL. We use react-router history.push
    // instead of window.location so React Router handles it as a normal SPA
    // navigation (no reload, no scroll jump).
    useEffect(() => {
        if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
        const handler = (event: MessageEvent) => {
            const data = event.data || {};
            if (data.type === 'mikasoft.navigate' && typeof data.url === 'string') {
                history.push(data.url);
            }
        };
        navigator.serviceWorker.addEventListener('message', handler);
        return () => navigator.serviceWorker.removeEventListener('message', handler);
    }, [history]);

    const handleClick = () => {
        history.push('/dashboard/notifications');
    };

    const hasUnread = unreadCount > 0;
    const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

    return (
        <div className="bell">
            <button
                type="button"
                className={`bell__trigger ${hasUnread ? 'has-unread' : ''}`}
                onClick={handleClick}
                aria-label={intl.formatMessage({ id: 'notifications.bell.label', defaultMessage: 'Notifications' })}
            >
                <BellIcon hasUnread={hasUnread} />
                {hasUnread && <span className="bell__badge">{badgeLabel}</span>}
            </button>
        </div>
    );
};

export default NotificationBell;
